// Hooks
import { useAlert } from '@hooks/useAlert';
import { useQuery } from '@hooks/useQuery';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useUserMutation, useUserQuery } from '@features/redux/apis/auth';

// Base Components
import { MutationEffect } from '@helpers/MutationEffect';
import { QueryEffect } from '@helpers/QueryEffect';

// Utils, Consts
import { Operation } from '@/types/general';
import { AppRoutes } from '@consts/routes';

// Types
import type { RootState } from '@features/redux/store';

export const UserEffects = () => {
    const userId = useQuery().get('id');
    const operation = userId ? Operation.Edit : Operation.Create;

    const navigate = useNavigate();

    const queryState = useUserQuery({ id: userId! }, { skip: !userId });
    const userEmail = useSelector((state: RootState) => state.auth.TokenPayload?.email);

    const { setAlert } = useAlert();
    const [, userMutationState] = useUserMutation({
        fixedCacheKey: operation === Operation.Edit ? 'UserMutation' : 'UserCreation',
    });

    return (
        <>
            <QueryEffect
                queryState={queryState}
                effects={{
                    isError: () => {
                        setAlert('Failed to fetch user.', 'error');
                        navigate(AppRoutes.Users);
                    },
                    isSuccess: () => {
                        if (queryState.data?.email === userEmail) {
                            setAlert('Cannot modify own user.', 'error');
                            navigate(AppRoutes.Users);
                        }
                    },
                }}
            />

            <MutationEffect
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                mutationState={userMutationState}
                effects={{
                    isError: () => {
                        setAlert(
                            `Problem ${
                                operation === Operation.Create ? 'creating' : 'editing'
                            } user. Please contact support at help@moutfitters.com`,
                            'error'
                        );
                        userMutationState.reset();
                    },
                    isSuccess: () => {
                        setAlert(
                            `Successfully ${operation === Operation.Create ? 'created' : 'updated'} user.`,
                            'success'
                        );
                        navigate(AppRoutes.Users);
                        userMutationState.reset();
                    },
                }}
            />
        </>
    );
};
